import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ProductFruits } from 'react-product-fruits'
import { config as appConfig } from '@/core'
import { AppToolBar, FullScreenProgress, Main, MobileNavBar } from '../../common/components'
import { SideBar } from './SideBar'
import { LoggedOutModal, useAuthUser } from '../../features/auth'
import { useViewport } from '../../hooks'
import { ToastProvider } from '@/hooks/toast'
import { SideBarLegacy } from './SideBarLegacy/SideBarLegacy'
import { ZendeskWidget } from './ZendeskWidget'

type LayoutProps = {
  children: React.ReactNode
  /**
   * Drops the sidebar and app bar. Everything else the layout owns - the auth guard, toasts and
   * the logged-out modal - still applies, so a standalone screen is not a second app shell.
   */
  standalone?: boolean
  /** Drops the space above the page, for a page that heads itself with the DS PageHeader. */
  flushTop?: boolean
  /** Gutters the page on the DS container padding rather than the app's narrower default. */
  wideGutters?: boolean
}

// The top padding clears the fixed app bar; without one there is nothing to clear.
const LayoutRoot = styled('div', { shouldForwardProp: (prop) => prop !== 'standalone' })<{ standalone?: boolean }>(
  ({ standalone }) => ({
    display: 'flex',
    flex: '1 1 auto',
    maxWidth: '100%',
    width: '100%',
    paddingTop: standalone ? 0 : 64,
    boxSizing: 'border-box',
    overflowX: 'hidden',
  }),
)

const contentBoxStyles = {
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column' as const,
  width: '100%',
  maxWidth: '100%',
  // The page canvas Figma names surface-neutral-25: the 2% step below
  // surface-default is what makes a content surface read as raised.
  backgroundColor: 'var(--colors-surface-canvas)',
  boxSizing: 'border-box' as const,
  overflowX: 'hidden' as const,
}

export const Layout = (props: LayoutProps): JSX.Element => {
  const { children, standalone = false, flushTop = false, wideGutters = false } = props

  const viewport = useViewport()
  const isMobile = viewport === 'mobile' || viewport === 'tablet'
  const [open, setOpen] = React.useState(!isMobile)
  const user = useAuthUser()
  const { i18n } = useTranslation('general')
  const [languageCode, setLanguageCode] = React.useState(i18n.language.split('-')[0])

  React.useEffect(() => {
    setLanguageCode(i18n.language.split('-')[0])
  }, [i18n.language])

  React.useEffect(() => {
    if (isMobile && open) {
      setOpen(false)
    } else if (!isMobile && !open) {
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  const rootUrl = new URL(document?.URL)
  const domain = rootUrl.hostname.replace('www.', '')
  const subDomain = domain.split('.')[0]

  // Early return AFTER all hooks
  if (!user?.token) {
    // Rendering placeholder page here while the user agent is redirected to
    // login.
    return <FullScreenProgress />
  }

  const handleDrawerToggle = () => {
    if (!isMobile) {
      setOpen(!open)
    }
  }

  const sidebarOpen = open
  const primaryRole = user?.roles?.[0] || ''
  const userInfo = {
    username: user?.username || '',
    role: primaryRole,
    group: {
      groupId: `${subDomain}${primaryRole}`,
    },
  }

  const handleForceOpen = () => {
    setOpen(true)
  }

  const isGlobalAdminSidebarEnabled = appConfig.features.enableGlobalAdminSidebar

  return (
    <LayoutRoot standalone={standalone}>
      {/* Toasts must outlive page navigations (e.g. create-cycle redirects then confirms). */}
      <ToastProvider position="bottom-center">
        <ProductFruits workspaceCode="hqvCOgDUYr5cOK8F" language={languageCode} user={userInfo} />
        <ZendeskWidget subDomain={subDomain} />

        {standalone ? null : isMobile ? (
          <MobileNavBar />
        ) : (
          <>
            <AppToolBar isOpen={sidebarOpen} onDrawerToggle={handleDrawerToggle} />
            {isGlobalAdminSidebarEnabled ? (
              <SideBar isOpen={sidebarOpen} onDrawerToggle={handleDrawerToggle} onForceOpen={handleForceOpen} />
            ) : (
              <SideBarLegacy
                isOpen={sidebarOpen}
                onDrawerToggle={handleDrawerToggle}
                handleForceOpen={handleForceOpen}
              />
            )}
          </>
        )}

        <Box sx={contentBoxStyles}>
          <Main flushTop={flushTop} wideGutters={wideGutters}>
            {children}
          </Main>
        </Box>
        <LoggedOutModal />
      </ToastProvider>
    </LayoutRoot>
  )
}
